import moment from "moment-timezone";
import {
  TRANSACTION_DATE_FILTER,
  FUNDED_TIMESTAMP_FILTER,
} from "./TransactionSearchPage.state";
import { parseTranslatedAmount, inferOperatorFromQuery } from "./amountFilter";
import { MULTI_VALUE_FILTERS } from "./FiltersForm";
// Shared with the panel so "mapped" here and "populated" there agree.
import { hasValue } from "./filterPresentation";

// Maps each parsed key from the `/transaction-search/translate` contract to criteria form key
const PARSED_TO_CRITERIA_KEY = {
  transactionNumber: "transactionId",
  amount: "amount",
  accountNumber: "accountNumber",
  cardholderName: "cardholderName",
  creditCardLastFour: "cardLastFour",
  bankAccountHolderName: "bankAccountHolderName",
  bankAccountLastFour: "bankLastFour",
};

// `paymentMode` stays single because its two values are mutually exclusive `serviceType` is currently a free-text scalar on the backend.
const PARSED_TO_FILTER_KEY = {
  paymentMode: "paymentMode",
  serviceType: "serviceType",
};

// Array-shaped parsed fields from the BE. Frontend stores canonical values comma-joined in the multi-select form rawValue (see MULTI_VALUE_FILTERS). Every element is applied.
const PARSED_ARRAY_TO_FILTER_KEY = {
  status: "paymentStatus",
  paymentMethod: "paymentMethod",
  paymentChannel: "paymentChannel",
  paymentSources: "paymentSource",
  agencies: "agencies",
};

const isParsedField = (v) => v && typeof v === "object" && "value" in v;

/**
 * normalizeFilterValue
 *
 * Find the canonical option value for a translated filter value. The model
 * sometimes returns a free-text label ("building permits") rather than the
 * backend's enum value ("building_permit"); pushing that unknown string into
 * the /payments query crashes the backend with a 502. Match by exact value
 * first, then case-insensitively by label, then fall back to null so the caller
 * skips applying the filter rather than sending a value the backend doesn't
 * recognize.
 * @param {string} value - The translated filter value to normalize.
 * @param {Array} options - The available filter options ({ value, text }).
 * @returns {string|null} - The canonical option value, or null when no match is
 * found.
 */
const normalizeFilterValue = (value, options) => {
  if (value == null || value === "" || !Array.isArray(options)) return null;
  const stringValue = String(value);
  const exact = options.find((opt) => opt.value === stringValue);
  if (exact) return exact.value;
  const lowered = stringValue.toLowerCase();
  const byLabel = options.find(
    (opt) =>
      opt.value !== "" &&
      opt.text != null &&
      String(opt.text).toLowerCase() === lowered
  );
  return byLabel ? byLabel.value : null;
};

/**
 * rescueMultiValueTokensFromUnmapped
 *
 * The current translate contract has single-slot fields like `paymentMethod` —
 * when the user says "cash and check payments", the model fills the slot with
 * one value and pushes the other onto `unmapped`, where it would be dropped
 * even though the form supports multi-select. This pass claims each unmapped
 * token for a multi-value filter using the same exact-value-then-label match
 * as the parsed path, first filter to accept it wins.
 * @param {Array} unmapped - The tokens the translate endpoint couldn't map.
 * @param {object} filterChoices - The available options keyed by form field.
 * @returns {object} - The rescued tokens grouped by form key.
 */
const rescueMultiValueTokensFromUnmapped = (unmapped, filterChoices) => {
  const tokensByFilter = {};
  const consumed = new Set();
  if (!Array.isArray(unmapped) || unmapped.length === 0) return tokensByFilter;
  MULTI_VALUE_FILTERS.forEach((formKey) => {
    const options = filterChoices?.[formKey];
    if (!Array.isArray(options)) return;
    unmapped.forEach((token) => {
      if (consumed.has(token)) return;
      const normalized = normalizeFilterValue(token, options);
      if (normalized == null) return;
      if (!tokensByFilter[formKey]) tokensByFilter[formKey] = [];
      tokensByFilter[formKey].push(normalized);
      consumed.add(token);
    });
  });
  return tokensByFilter;
};

/**
 * applyTranslateResultToForms
 *
 * Writes a translate result into the criteria and filter forms and the date
 * range, resetting every known field first so a value the new query does not
 * mention cannot survive. Reports what it wrote, not what the model returned:
 * values the backend does not recognize are skipped.
 * @param {object} result - The translate result.
 * @param {object} options - Form actions, choices, date setters and context.
 * @returns {object} - `{ mappedFilters, mappedDateRange }`.
 */
export const applyTranslateResultToForms = (
  result,
  {
    criteriaActions,
    filtersActions,
    filterChoices,
    setNlSearchDates,
    resetSearchDatesToDefault,
    selectDateFilter,
    timezone,
    originalQuery,
  }
) => {
  const nothingMapped = { mappedFilters: false, mappedDateRange: false };
  if (!result || !result.parsed) return nothingMapped;
  const parsed = result.parsed;
  let mappedFilters = false;

  // reset every known field before applying new parsed result so populated fields from stale query that aren't in new query don't stay set
  Object.values(PARSED_TO_CRITERIA_KEY).forEach((formKey) => {
    criteriaActions.fields[formKey]?.set("");
  });
  Object.values(PARSED_TO_FILTER_KEY).forEach((formKey) => {
    filtersActions.fields[formKey]?.set("");
  });
  Object.values(PARSED_ARRAY_TO_FILTER_KEY).forEach((formKey) => {
    filtersActions.fields[formKey]?.set("");
  });
  // The authoritative reset for this round trip. The range goes back to the
  // default (a year ago through today), not to an empty range: the filter sits
  // beside the search field and stays populated unless this result carries a
  // range of its own, which the block at the end of this function applies
  // (WCHK-3083).
  resetSearchDatesToDefault();

  Object.entries(PARSED_TO_CRITERIA_KEY).forEach(([parsedKey, formKey]) => {
    const field = parsed[parsedKey];
    if (!isParsedField(field)) return;
    // amount supports operator and range expressions ("> 300", "200 - 400"); every other criterion is a literal value
    if (parsedKey === "amount") {
      let normalized = parseTranslatedAmount(field);
      // in case translate endpoint not surfacing operator, recovers the operator from original query
      if (normalized !== "" && originalQuery) {
        normalized = inferOperatorFromQuery(normalized, originalQuery);
      }
      if (normalized !== "") {
        criteriaActions.fields[formKey]?.set(normalized);
        mappedFilters = true;
      }
    } else if (hasValue(field.value)) {
      criteriaActions.fields[formKey]?.set(field.value);
      mappedFilters = true;
    }
  });

  const rescuedByFilter = rescueMultiValueTokensFromUnmapped(
    result.unmapped,
    filterChoices
  );

  // combine canonical values from three sources before writing to the form: (a) parsed single-value fields, (b) parsed array fields, and (c) tokens rescued from `unmapped`. Multi-select fields store rawValue as a comma- separated string; The dedupe-then-join lets a parsed value and a rescued sibling token coexist in the same chip (e.g. parsed paymentMethod=cash + rescued "check" → "cash,check").
  const valuesByFilter = {};
  const accumulate = (formKey, value) => {
    if (value == null) return;
    if (!valuesByFilter[formKey]) valuesByFilter[formKey] = [];
    if (!valuesByFilter[formKey].includes(value)) {
      valuesByFilter[formKey].push(value);
    }
  };

  Object.entries(PARSED_TO_FILTER_KEY).forEach(([parsedKey, formKey]) => {
    const field = parsed[parsedKey];
    if (!isParsedField(field)) return;
    accumulate(formKey, normalizeFilterValue(field.value, filterChoices?.[formKey]));
  });

  Object.entries(PARSED_ARRAY_TO_FILTER_KEY).forEach(([parsedKey, formKey]) => {
    const list = parsed[parsedKey];
    if (!Array.isArray(list)) return;
    list.forEach((item) => {
      if (!isParsedField(item)) return;
      accumulate(
        formKey,
        normalizeFilterValue(item.value, filterChoices?.[formKey])
      );
    });
  });

  Object.entries(rescuedByFilter).forEach(([formKey, tokens]) => {
    tokens.forEach((token) => accumulate(formKey, token));
  });

  Object.entries(valuesByFilter).forEach(([formKey, values]) => {
    if (values.length === 0) return;
    filtersActions.fields[formKey]?.set(values.join(","));
    mappedFilters = true;
  });

  const dateRange = parsed.dateRange;
  const mappedDateRange = Boolean(dateRange && dateRange.start && dateRange.end);
  if (mappedDateRange) {
    // The translate endpoint resolved relative dates ("last 30 days") against `timezone`, so anchor moments we hand to the date picker to that same zone
    const start = timezone
      ? moment.tz(dateRange.start, timezone)
      : moment(dateRange.start);
    const end = timezone
      ? moment.tz(dateRange.end, timezone)
      : moment(dateRange.end);
    setNlSearchDates({ startDate: start, endDate: end });
    selectDateFilter(
      dateRange.basis === "settlement"
        ? FUNDED_TIMESTAMP_FILTER
        : TRANSACTION_DATE_FILTER
    );
  }

  return { mappedFilters, mappedDateRange };
};
